import type { HTMLChakraProps } from '@chakra-ui/react';
import { Box, Flex } from '@chakra-ui/react';
import React from 'react';

import type { DecodedInput } from 'types/api/decodedInput';
import type { ArrayElement } from 'types/utils';

import { Skeleton } from 'toolkit/chakra/skeleton';
import CopyToClipboard from 'ui/shared/CopyToClipboard';
import AddressEntity from 'ui/shared/entities/address/AddressEntity';
import TruncatedValue from 'ui/shared/TruncatedValue';

interface Props {
  data: DecodedInput['parameters'];
  isLoading?: boolean;
}

const HeaderItem = ({ children, isLoading, ...rest }: { children: React.ReactNode; isLoading?: boolean } & HTMLChakraProps<'div'>) => {
  return (
    <Skeleton
      fontWeight={ 400 }
      color="table.header.fg"
      pb={ 1 }
      display="inline-block"
      width="fit-content"
      height="fit-content"
      loading={ isLoading }
      { ...rest }
    >
      { children }
    </Skeleton>
  );
};

const Row = ({ name, type, indexed, value, isLoading }: ArrayElement<DecodedInput['parameters']> & { isLoading?: boolean }) => {
  const content = (() => {
    if (type === 'address' && typeof value === 'string') {
      return (
        <AddressEntity
          address={{ hash: value, name: '' }}
          isLoading={ isLoading }
        />
      );
    }

    if (typeof value === 'object') {
      const text = JSON.stringify(value, undefined, 4);
      return (
        <Flex alignItems="flex-start" whiteSpace="normal" wordBreak="break-all">
          <TruncatedValue value={ text } isLoading={ isLoading }/>
          <CopyToClipboard text={ text } isLoading={ isLoading }/>
        </Flex>
      );
    }

    return (
      <Flex alignItems="flex-start" whiteSpace="normal" wordBreak="break-all">
        <TruncatedValue value={ value } isLoading={ isLoading }/>
        <CopyToClipboard text={ value } isLoading={ isLoading }/>
      </Flex>
    );
  })();

  return (
    <Flex py="10px" px={ 4 } borderWidth="1px" borderColor="table.cell.border" borderRadius="base">
      <TruncatedValue maxW="12.5%" flex={ 1 } value={ name } isLoading={ isLoading }/>
      <TruncatedValue maxW="12.5%" flex={ 1 } value={ type } isLoading={ isLoading }/>
      { indexed !== undefined && (
        <Skeleton maxW="12.5%" flex={ 1 } loading={ isLoading } display="inline-block">{ indexed ? 'true' : 'false' }</Skeleton>
      ) }
      <Skeleton maxW="50%" flex={ 1 } loading={ isLoading } display="inline-block">{ content }</Skeleton>
    </Flex>
  );
};

const LogDecodedInputDataTable = ({ data, isLoading }: Props) => {
  const hasIndexed = data.some(({ indexed }) => indexed !== undefined);

  return (
    <Box
      textStyle="sm"
    >
      <Flex borderRadius="base" py="10px" px={ 4 } bg="table.header.bg" mb={ 2 }>
        <HeaderItem maxW="12.5%" flex={ 1 } isLoading={ isLoading }>Name</HeaderItem>
        <HeaderItem maxW="12.5%" flex={ 1 } isLoading={ isLoading }>Type</HeaderItem>
        { hasIndexed && <HeaderItem maxW="12.5%" flex={ 1 } isLoading={ isLoading }>Inde<wbr/>xed?</HeaderItem> }
        <HeaderItem maxW="50%" flex={ 1 } isLoading={ isLoading }>Data</HeaderItem>
      </Flex>
      <Flex flexDir="column" gap={ 2 }>
        { data.map((item) => {
          return <Row key={ item.name } { ...item } isLoading={ isLoading }/>;
        }) }
      </Flex>
    </Box>
  );
};

export default LogDecodedInputDataTable;
